<script>
import moment from "moment";
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";
import { useLeadMetaStore } from '@/stores/useLeadMetaStore';

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "HRM",
      text1: "Late Comers Report",
      clientAdmin: clientAdmin(),
      filter: {
        month: Number(moment().format('M')),
        year: Number(moment().format('YYYY')),
        branch_id: '',
        department_id: '',
        employee_id: '',
      },
      months: [
        { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
        { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
        { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
        { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' },
      ],
      years: [],
      data: [],
      departments: [],
      employees: [],
      expandedRows: [],
    };
  },

  computed: {
    branches() {
      return useLeadMetaStore().branches || [];
    },

    totalEmployees() {
      return this.data.length;
    },

    totalLateInstances() {
      return this.data.reduce((sum, row) => sum + (row.late_count || 0), 0);
    },

    avgLateCount() {
      if (!this.data.length) return 0;
      return (this.totalLateInstances / this.data.length).toFixed(1);
    },

    maxLateCount() {
      if (!this.data.length) return 0;
      return Math.max(...this.data.map(r => r.late_count || 0));
    },

    mostLateEmployee() {
      if (!this.data.length) return null;
      return [...this.data].sort((a, b) => (b.late_count || 0) - (a.late_count || 0))[0];
    },
  },

  mounted() {
    const currentYear = moment().year();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.years.push({ id: y, name: String(y) });
    }
    this.getDropdowns();
    this.filterReport();
  },

  methods: {

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv', ...this.filter };
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/hrms/reports/late-comers?${query_string}`, {
          responseType: 'blob',
        });
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `late-comers-report-${this.filter.month}-${this.filter.year}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport() {
      this.errors = {};
      this.setLoading(true);
      try {
        const query_string = new URLSearchParams(this.filter).toString();
        const response = await axios.get(`/hrms/reports/late-comers?${query_string}`);
        this.data = response.data.data || [];
        this.expandedRows = [];
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      await useLeadMetaStore().loadAll();
      try {
        const [deptRes, empRes] = await Promise.allSettled([
          axios.get('/hrms/departments/dropdown'),
          axios.get('/hrms/employees', { params: { per_page: 1000 } }),
        ]);
        this.departments = deptRes.status === 'fulfilled' ? deptRes.value.data.data : [];
        this.employees = empRes.status === 'fulfilled' ? empRes.value.data.data.data : [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    hasActiveFilter() {
      const currentMonth = String(moment().month() + 1);
      const currentYear = String(moment().year());
      return this.filter.branch_id || this.filter.department_id || this.filter.employee_id
        || String(this.filter.month) !== currentMonth || String(this.filter.year) !== currentYear;
    },

    clearFilter() {
      this.filter.branch_id = '';
      this.filter.department_id = '';
      this.filter.employee_id = '';
      this.filter.month = moment().format('M');
      this.filter.year = moment().format('YYYY');
      this.filterReport();
    },

    toggleDates(employeeId) {
      const idx = this.expandedRows.indexOf(employeeId);
      if (idx > -1) {
        this.expandedRows.splice(idx, 1);
      } else {
        this.expandedRows.push(employeeId);
      }
    },

    isExpanded(employeeId) {
      return this.expandedRows.includes(employeeId);
    },

    lateBadgeClass(count) {
      const c = parseInt(count || 0);
      if (c >= 5) return 'bg-danger-subtle text-danger';
      if (c >= 3) return 'bg-warning-subtle text-warning';
      return 'bg-info-subtle text-info';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3">
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Employees with Late Marks</span>
              <span class="fw-bold text-warning" style="font-size:20px;">{{ totalEmployees }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Late Instances</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ totalLateInstances }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Late Count</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ avgLateCount }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Most Late</span>
              <span class="fw-bold text-danger" style="font-size:16px;">
                {{ mostLateEmployee ? mostLateEmployee.employee_name : '-' }}
              </span>
              <small v-if="mostLateEmployee" class="text-muted d-block" style="font-size:10px;">
                {{ mostLateEmployee.late_count }} times
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.month" :options="months" label="name" :reduce="m => m.id"
                placeholder="Month" @update:modelValue="filterReport()" style="min-width: 140px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.year" :options="years" label="name" :reduce="y => y.id"
                placeholder="Year" @update:modelValue="filterReport()" style="min-width: 120px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.branch_id" :options="branches" label="name" :reduce="b => b.id"
                placeholder="Branch" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.department_id" :options="departments" label="name" :reduce="d => d.id"
                placeholder="Department" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.employee_id" :options="employees" label="full_name" :reduce="e => e.id"
                placeholder="Employee" :clearable="true" @update:modelValue="filterReport()" style="min-width: 180px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Code</th>
                    <th>Late Count</th>
                    <th>Late Dates</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.employee_id">
                    <td>{{ index + 1 }}.</td>
                    <td><strong class="text-dark">{{ row.employee_name }}</strong></td>
                    <td>{{ row.employee_code }}</td>
                    <td>
                      <span class="badge rounded-pill" :class="lateBadgeClass(row.late_count)">{{ row.late_count }}</span>
                    </td>
                    <td>
                      <template v-if="row.dates && row.dates.length">
                        <span v-for="(d, i) in (isExpanded(row.employee_id) ? row.dates : row.dates.slice(0, 3))" :key="i"
                          class="badge bg-light text-dark border me-1 mb-1" style="font-size:10.5px; font-weight:500;">
                          {{ formatDate(d) }}
                        </span>
                        <a v-if="row.dates.length > 3" href="javascript:void(0);" class="small text-primary"
                          @click="toggleDates(row.employee_id)">
                          {{ isExpanded(row.employee_id) ? 'Show less' : `+${row.dates.length - 3} more` }}
                        </a>
                      </template>
                      <span v-else class="text-muted">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>